import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { BottomNav } from "@/components/BottomNav";
import { LogoMark } from "@/components/LogoMark";
import { ChevronLeft } from "lucide-react";

export const SectionLayout = ({ id, icon: Icon, children }) => {
  const { t } = useLang();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  return (
    <div className="min-h-[100svh] bg-[#F8FAFC]" data-testid={`section-page-${id}`}>
      {/* Sticky header */}
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/92 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-xl items-center justify-between px-3 py-2.5 sm:px-6">
          <button
            onClick={() => navigate("/")}
            data-testid="back-button"
            aria-label={t("backToJourney")}
            className="inline-flex items-center gap-1 rounded-full py-1.5 pl-1.5 pr-3 text-sm font-semibold text-[#0A192F] transition-colors hover:bg-slate-100 active:scale-95"
          >
            <ChevronLeft size={20} strokeWidth={2.25} />
            {t("back")}
          </button>
          <LogoMark className="h-6" />
          <LanguageSwitcher variant="dark" />
        </div>
      </header>

      {/* Title */}
      <div className="mx-auto w-full max-w-xl px-5 pt-8 sm:px-6">
        <div className="flex items-center gap-4 animate-fade-up">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-[#0A192F] text-[#E9CE7D]">
            <Icon size={24} strokeWidth={1.6} />
          </span>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#0A192F] leading-none">
            {t(`sec.${id}.title`)}
          </h1>
        </div>
      </div>

      {/* Body */}
      <div
        className="mx-auto w-full max-w-xl px-5 pb-28 pt-7 sm:px-6 animate-fade-up"
        style={{ animationDelay: "0.08s" }}
      >
        {children}
      </div>

      <BottomNav />
    </div>
  );
};

export const SectionIntro = ({ children }) =>
  children ? <p className="mb-7 text-[15px] leading-[1.7] text-slate-600">{children}</p> : null;

export const Panel = ({ children, className = "", ...props }) => (
  <div className={`maqam-card p-6 ${className}`} {...props}>
    {children}
  </div>
);
